import React from "react";
import { ICellRendererParams } from "ag-grid-community";
import { useHistory } from "react-router-dom";
import { Flex, Text } from "@jsq/ds/core";
import { useGetPaymentBeneficiaryRiskRatingForCaseQuery } from "~/graphql/supergraphHooks";
import { RiskRatingStatus } from "../cellStatus/RiskRatingStatus";
import { RiskStatusType } from "../cellStatus/RiskRatingStatus/types";
import { PositionData } from "../types";
import { getPositionRiskRating } from "../utils/riskRatingCalculations";
import { cellContainer, cellText } from "./styles_vanilla.css";
interface RiskRatingCellRendererProps extends ICellRendererParams {
  data: PositionData;
  picrRevampEnabled: boolean;
}

const RiskRatingCellRenderer: React.FC<RiskRatingCellRendererProps> = ({
  data,
  picrRevampEnabled,
}) => {
  const history = useHistory();

  const caseId = history.location.pathname.split("/case/")[1]?.split("/")[0];
  const programId = data?.linkedOnboardingCase?.program.id ?? 0;
  const linkedOnboardingCaseId = data?.linkedOnboardingCase?.id ?? 0;

  const proposedBeneficiaryProfileId = data?.proposedPaymentAccount?.profileId;
  const matchingProfile = data?.allLinkedProfiles?.find(
    (p) => p.id === proposedBeneficiaryProfileId,
  );

  const { data: createdProfileRiskRatingData } = useGetPaymentBeneficiaryRiskRatingForCaseQuery(
    {
      variables: {
        caseId: parseInt(caseId),
        programId: programId,
      },
      skip: !!matchingProfile || !picrRevampEnabled || !programId || !data,
    },
  );

  const { data: existingBeneficiaryRiskRatingData } =
    useGetPaymentBeneficiaryRiskRatingForCaseQuery({
      variables: {
        caseId: linkedOnboardingCaseId,
        programId: programId,
      },
      skip: !picrRevampEnabled || !programId || !data,
    });

  const existingBeneficiaryRiskRating =
    existingBeneficiaryRiskRatingData?.complianceCase?.paymentAccount?.profile?.riskRating;

  const createdProfileRiskRating =
    createdProfileRiskRatingData?.complianceCase?.paymentAccount?.profile?.riskRating;
  const newBeneficiaryRiskRating = matchingProfile
    ? matchingProfile?.riskRating
    : createdProfileRiskRating;

  if (!data) return null;

  const {
    simulatedRiskRating,
    linkedProfile,
    successorCases,
    parentId,
    complianceCaseName,
    investmentEntity,
  } = data;

  const positionName = `${complianceCaseName} in ${investmentEntity.name}`;

  let currentRiskRating: string | null | undefined;
  let newRiskRating: string | null | undefined;

  if (picrRevampEnabled) {
    // Use comprehensive position risk rating calculation
    const investorRiskRating = data.linkedOnboardingCase?.investingProfile?.riskRating;

    const existingPaymentAccountRiskRating =
      data.linkedOnboardingCase?.paymentAccount?.riskRating;
    const newPaymentAccountRiskRating = data.proposedPaymentAccount?.riskRating;

    if (investorRiskRating) {
      currentRiskRating = getPositionRiskRating(
        investorRiskRating,
        existingBeneficiaryRiskRating,
        existingPaymentAccountRiskRating,
      );
    } else {
      currentRiskRating = undefined;
    }

    if (investorRiskRating && newBeneficiaryRiskRating && newPaymentAccountRiskRating) {
      newRiskRating = getPositionRiskRating(
        investorRiskRating,
        newBeneficiaryRiskRating,
        newPaymentAccountRiskRating,
      );
    } else {
      newRiskRating = undefined;
    }
  } else {
    // Legacy: Get the current risk rating from simulatedRiskRating
    currentRiskRating = simulatedRiskRating?.investorProfileRiskRating;

    // Legacy: Get the new risk rating from linkedProfile, preferring overriddenRating if available
    newRiskRating =
      data.linkedProfile?.riskRating?.overriddenRating ||
      data.linkedProfile?.riskRating?.calculatedRating;
  }
  const isGroupRow = successorCases && successorCases.length > 0;
  const isChildRow = parentId !== undefined;
  const isNotAttached =
    !data.linkedOnboardingCase ||
    ((!data.linkedOnboardingCase.profiles || data.linkedOnboardingCase.profiles.length === 0) &&
      !data.linkedOnboardingCase.investingProfile);

  // Return empty cell for parent rows
  if (isGroupRow) {
    return (
      <Flex className={cellContainer}>
        <Text className={cellText}></Text>
      </Flex>
    );
  }

  if (isNotAttached || isChildRow) {
    return <RiskRatingStatus status={RiskStatusType.NOT_APPLICABLE} />;
  }

  // Show select payment beneficiary for normal rows when linkedProfile is null
  if (
    !isGroupRow &&
    (!successorCases || successorCases.length === 0) &&
    (picrRevampEnabled
      ? data?.proposedPaymentAccount?.profileId == null
      : linkedProfile == null)
  ) {
    return <RiskRatingStatus status={RiskStatusType.SELECT_PAYMENT_BENEFICIARY} />;
  }

  // Function to determine the risk status type
  // For picrRevampEnabled: Display based on NEW/proposed risk rating with change indicator
  // For legacy: Display based on CURRENT/existing risk rating
  const getRiskStatus = (
    current: string | null | undefined,
    next?: string | null,
  ): RiskStatusType => {
    if (!current) return RiskStatusType.NOT_DETERMINED;
    if (isChildRow) return RiskStatusType.NOT_APPLICABLE;

    const riskLevels = { low: 1, medium: 2, high: 3, halt: 4 };
    const currentLevel = riskLevels[current as keyof typeof riskLevels];

    if (!next) {
      switch (current) {
        case "low":
          return RiskStatusType.LOW_NONE;
        case "medium":
          return RiskStatusType.MEDIUM_NONE;
        case "high":
          return RiskStatusType.HIGH_NONE;
        case "halt":
          return RiskStatusType.HALT_NONE;
        default:
          return RiskStatusType.NOT_DETERMINED;
      }
    }

    const nextLevel = riskLevels[next as keyof typeof riskLevels];
    const levelDiff = nextLevel - currentLevel;

    if (picrRevampEnabled) {
      // determine the riskRating on the proposed changes(i.e. newRiskRating)
      switch (next) {
        case "low":
          if (levelDiff === 0) return RiskStatusType.LOW_UNCHANGED;
          return levelDiff < -1 ? RiskStatusType.LOW_DECREASE_TWO : RiskStatusType.LOW_DECREASE;
        case "medium":
          if (levelDiff === 0) return RiskStatusType.MEDIUM_UNCHANGED;
          return levelDiff > 0
            ? RiskStatusType.MEDIUM_INCREASE
            : RiskStatusType.MEDIUM_DECREASE;
        case "high":
          if (levelDiff === 0) return RiskStatusType.HIGH_UNCHANGED;
          return levelDiff > 0 ? RiskStatusType.HIGH_INCREASE : RiskStatusType.HIGH_DECREASE;
        case "halt":
          return RiskStatusType.HALT_NONE;
        default:
          return RiskStatusType.NOT_DETERMINED;
      }
    } else {
      // Legacy logic: Display the CURRENT/EXISTING risk rating
      switch (current) {
        case "low":
          return levelDiff === 0
            ? RiskStatusType.LOW_UNCHANGED
            : levelDiff < -1
              ? RiskStatusType.LOW_DECREASE_TWO
              : RiskStatusType.LOW_DECREASE;
        case "medium":
          return levelDiff === 0
            ? RiskStatusType.MEDIUM_UNCHANGED
            : levelDiff > 0
              ? RiskStatusType.MEDIUM_INCREASE
              : RiskStatusType.MEDIUM_DECREASE;
        case "high":
          return levelDiff === 0 ? RiskStatusType.HIGH_UNCHANGED : RiskStatusType.HIGH_INCREASE;
        case "halt":
          return RiskStatusType.HALT_NONE;
        default:
          return RiskStatusType.NOT_DETERMINED;
      }
    }
  };

  const riskRatingData = {
    linkedProfile: linkedProfile
      ? {
          riskRating: linkedProfile.riskRating,
          paymentAccounts: linkedProfile.paymentAccounts,
        }
      : undefined,
    simulatedRiskRating: data.simulatedRiskRating,
    linkedOnboardingCase: data.linkedOnboardingCase
      ? {
          investingProfile: data.linkedOnboardingCase.investingProfile
            ? {
                riskRating: data.linkedOnboardingCase.investingProfile.riskRating,
                paymentAccounts: Array.isArray(
                  data.linkedOnboardingCase.investingProfile.paymentAccounts,
                )
                  ? data.linkedOnboardingCase.investingProfile.paymentAccounts
                  : [],
              }
            : undefined,
          profiles: data.linkedOnboardingCase.profiles,
          paymentAccount: data.linkedOnboardingCase.paymentAccount,
        }
      : undefined,
  };

  const newRiskRatingData = {
    linkedOnboardingCase: data.linkedOnboardingCase,
    currentPaymentAccount: data.proposedPaymentAccount,
    existingBeneficiaryRiskRating: existingBeneficiaryRiskRating,
    newBeneficiaryRiskRating: newBeneficiaryRiskRating,
  };

  return (
    <RiskRatingStatus
      status={getRiskStatus(currentRiskRating, newRiskRating)}
      data={riskRatingData}
      picrRevampEnabled={picrRevampEnabled}
      newData={newRiskRatingData}
      currentRiskRating={currentRiskRating}
      newRiskRating={newRiskRating ?? undefined}
      positionName={positionName}
    />
  );
};

export default RiskRatingCellRenderer;
